import { Box, FormControl, FormLabel, Input, Select, Stack, Text, VStack } from '@chakra-ui/react';
import { useState, useEffect } from 'react';
import { useWatch } from 'react-hook-form';
import { TaskType } from '@/lib/sdk/chatbot/types';

const VALIDATION_TYPE_OPTIONS = [
  { value: 'less_than', label: 'Less than' },
  { value: 'greater_than', label: 'Greater than' },
  { value: 'greater_than_or_equal', label: 'Greater than or equal to' },
  { value: 'less_than_or_equal', label: 'Less than or equal to' },
  { value: 'between', label: 'Between (inclusive)' },
];

interface ValidationConfigSectionProps {
  control: any;
  setValue: any;
  existingTasks: { id: string; name: string; index: number; type: string }[];
  currentTaskIndex?: number;
}

export function ValidationConfigSection({
  control,
  setValue,
  existingTasks,
  currentTaskIndex,
}: ValidationConfigSectionProps) {
  const validationConfig = useWatch({ control, name: 'validationConfig' }) ?? {};
  const [validationSource, setValidationSource] = useState<'static' | 'dynamic'>('static');

  useEffect(() => {
    if (validationConfig.reference_task !== undefined) {
      setValidationSource('dynamic');
    } else if (validationConfig.threshold !== undefined) {
      setValidationSource('static');
    }
  }, [validationConfig.reference_task, validationConfig.threshold]);

  const handleSourceChange = (source: 'static' | 'dynamic') => {
    setValidationSource(source);
    if (source === 'static') {
      setValue('validationConfig.reference_task', undefined);
    } else {
      setValue('validationConfig.threshold', undefined);
    }
  };

  const numberTasksBeforeCurrent = existingTasks.filter(
    (t) => t.type === TaskType.NUMBER && (currentTaskIndex === undefined || t.index < currentTaskIndex)
  );

  return (
    <VStack spacing={4} align="stretch">
      <FormControl>
        <FormLabel color="text-primary" fontWeight="medium" fontSize="sm">
          Validation Type
        </FormLabel>
        <Select
          placeholder="No validation"
          bg="bg-surface"
          size="sm"
          value={validationConfig.type ?? ''}
          data-testid="wfv4-config-validation-type-select"
          onChange={(e) => {
            const val = e.target.value || undefined;
            setValue('validationConfig.type', val);
            if (!val) {
              setValue('validationConfig.threshold', undefined);
              setValue('validationConfig.max_threshold', undefined);
              setValue('validationConfig.reference_task', undefined);
              setValue('validationConfig.error_message', undefined);
            } else if (val === 'between') {
              // "Between" only supports two static bounds — a reference task gives one
              // dynamic value, not a range, so drop it if it was set for another type.
              setValue('validationConfig.reference_task', undefined);
            }
          }}
        >
          {VALIDATION_TYPE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </Select>
      </FormControl>

      {validationConfig.type === 'between' && (
        <Stack direction={{ base: 'column', sm: 'row' }} spacing={3} align={{ base: 'stretch', sm: 'flex-start' }}>
          <FormControl flex={1}>
            <FormLabel fontSize="sm" color="text-secondary">Minimum (inclusive)</FormLabel>
            <Input
              type="number"
              placeholder="Enter value"
              bg="bg-surface"
              size="sm"
              value={validationConfig.threshold !== undefined ? validationConfig.threshold : ''}
              onChange={(e) => {
                const val = e.target.value;
                setValue('validationConfig.threshold', val === '' ? undefined : parseFloat(val));
              }}
              data-testid="wfv4-config-validation-min-input"
            />
          </FormControl>
          <FormControl flex={1}>
            <FormLabel fontSize="sm" color="text-secondary">Maximum (inclusive)</FormLabel>
            <Input
              type="number"
              placeholder="Enter value"
              bg="bg-surface"
              size="sm"
              value={validationConfig.max_threshold !== undefined ? validationConfig.max_threshold : ''}
              onChange={(e) => {
                const val = e.target.value;
                setValue('validationConfig.max_threshold', val === '' ? undefined : parseFloat(val));
              }}
              data-testid="wfv4-config-validation-max-input"
            />
          </FormControl>
        </Stack>
      )}

      {validationConfig.type && validationConfig.type !== 'between' && (
        <Stack direction={{ base: 'column', sm: 'row' }} spacing={3} align={{ base: 'stretch', sm: 'flex-start' }}>
          <FormControl flex={1}>
            <FormLabel fontSize="sm" color="text-secondary">Validation Source</FormLabel>
            <Select
              bg="bg-surface"
              size="sm"
              value={validationSource}
              onChange={(e) => handleSourceChange(e.target.value as 'static' | 'dynamic')}
            >
              <option value="static">Static Value</option>
              <option value="dynamic">Previous Task</option>
            </Select>
          </FormControl>

          {validationSource === 'static' ? (
            <FormControl flex={1}>
              <FormLabel fontSize="sm" color="text-secondary">Threshold</FormLabel>
              <Input
                type="number"
                placeholder="Enter value"
                bg="bg-surface"
                size="sm"
                value={validationConfig.threshold !== undefined ? validationConfig.threshold : ''}
                onChange={(e) => {
                  const val = e.target.value;
                  setValue('validationConfig.threshold', val === '' ? undefined : parseFloat(val));
                }}
              />
            </FormControl>
          ) : (
            <FormControl flex={1}>
              <FormLabel fontSize="sm" color="text-secondary">Reference Task</FormLabel>
              <Select
                placeholder="Select number task"
                bg="bg-surface"
                size="sm"
                value={validationConfig.reference_task !== undefined ? validationConfig.reference_task : ''}
                onChange={(e) => {
                  const val = e.target.value;
                  setValue('validationConfig.reference_task', val === '' ? undefined : parseInt(val, 10));
                }}
              >
                {numberTasksBeforeCurrent.map((t) => (
                  <option key={t.id} value={t.index}>{t.name} (Task {t.index})</option>
                ))}
              </Select>
              <Text fontSize="xs" color="text-muted" mt={1}>Compare against a previous number task's value</Text>
            </FormControl>
          )}
        </Stack>
      )}

      {validationConfig.type && (
        <FormControl>
          <FormLabel fontSize="sm" color="text-secondary">Error Message (optional)</FormLabel>
          <Input
            placeholder="e.g. Please enter a valid 10-digit mobile number"
            bg="bg-surface"
            size="sm"
            value={validationConfig.error_message ?? ''}
            onChange={(e) => setValue('validationConfig.error_message', e.target.value || undefined)}
            data-testid="wfv4-config-validation-error-message-input"
          />
          <Text fontSize="xs" color="text-muted" mt={1}>
            Sent when the entered number fails validation. Leave blank to use the default message.
          </Text>
        </FormControl>
      )}
    </VStack>
  );
}
